import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Loader2, CheckCircle, XCircle, Ban, ShieldAlert, Search } from "lucide-react";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";

const statusColors = {
  ACTIVE: "bg-green-100 text-green-800", PENDING_APPROVAL: "bg-amber-100 text-amber-800",
  SUSPENDED: "bg-red-100 text-red-800", REJECTED: "bg-slate-100 text-slate-600",
};

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [actionDialog, setActionDialog] = useState(null); // {provider_id, action, name}
  const [saving, setSaving] = useState(false);

  const fetchProviders = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const res = await api.get("/admin/providers", { params });
      setProviders(res.data);
    } catch { setProviders([]); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); fetchProviders(); }, [filter]); // eslint-disable-line

  const handleStatusChange = async () => {
    if (!actionDialog) return;
    setSaving(true);
    try {
      await api.put(`/admin/providers/${actionDialog.provider_id}/status`, { status: actionDialog.newStatus });
      toast.success(`Provider ${actionDialog.newStatus.toLowerCase().replace("_", " ")}`);
      setActionDialog(null);
      fetchProviders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const typeOptions = Array.from(new Set(providers.map((provider) => provider.type).filter(Boolean))).sort();
  const filteredProviders = useMemo(() => providers.filter((provider) => {
    const matchesSearch = !query || [provider.name, provider.user_name, provider.user_email, provider.city].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase());
    const matchesType = typeFilter === "all" || provider.type === typeFilter;
    return matchesSearch && matchesType;
  }), [providers, query, typeFilter]);
  const activeFilters = [
    query ? { key: "search", label: `Search: ${query}`, onRemove: () => setQuery("") } : null,
    filter !== "all" ? { key: "status", label: `Status: ${filter}`, onRemove: () => setFilter("all") } : null,
    typeFilter !== "all" ? { key: "type", label: `Type: ${typeFilter}`, onRemove: () => setTypeFilter("all") } : null,
  ].filter(Boolean);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-providers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Building2 className="w-8 h-8 text-court-orange" />Providers
          </h1>
          <p className="text-muted-foreground mt-1">Manage provider approvals and status</p>
        </div>
      </div>

      <AdminFilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search providers by organization, owner, city"
        searchTestId="provider-search-input"
        filters={[
          { key: "status", label: "Status", value: filter, onChange: setFilter, options: [{ value: "all", label: "All Statuses" }, { value: "ACTIVE", label: "Active" }, { value: "PENDING_APPROVAL", label: "Pending Approval" }, { value: "SUSPENDED", label: "Suspended" }, { value: "REJECTED", label: "Rejected" }], testId: "provider-status-filter" },
          { key: "type", label: "Type", value: typeFilter, onChange: setTypeFilter, options: [{ value: "all", label: "All Types" }, ...typeOptions.map((type) => ({ value: type, label: type }))], testId: "provider-type-filter" },
        ]}
        activeFilters={activeFilters}
        onClearAll={() => { setQuery(""); setFilter("all"); setTypeFilter("all"); }}
        extraActions={<Button variant="outline" className="rounded-md" data-testid="provider-search-btn"><Search className="w-4 h-4 mr-2" />Refine</Button>}
      />

      {filteredProviders.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No providers found</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-100 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading uppercase tracking-wider text-xs">Organization</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Owner</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Type</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-center">Venues</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-center">Bookings</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProviders.map(p => (
                <TableRow key={p.provider_id} data-testid={`provider-row-${p.provider_id}`}>
                  <TableCell>
                    <p className="font-medium">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.city || "No city"}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{p.user_name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.user_email}</p>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] font-heading uppercase">{p.type}</Badge></TableCell>
                  <TableCell className="text-center font-heading font-bold">{p.venues_count}</TableCell>
                  <TableCell className="text-center font-heading font-bold">{p.bookings_count}</TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[p.status] || 'bg-slate-100'} text-[10px] font-heading uppercase`}>
                      {p.status?.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1.5 justify-end">
                      {p.status !== "ACTIVE" && (
                        <Button data-testid={`approve-${p.provider_id}`} size="sm" onClick={() => setActionDialog({ provider_id: p.provider_id, name: p.name, newStatus: "ACTIVE", label: "Approve" })}
                          className="h-7 bg-green-600 hover:bg-green-700 text-white rounded-full font-heading uppercase text-[10px] px-3">
                          <CheckCircle className="w-3 h-3 mr-1" />Approve
                        </Button>
                      )}
                      {p.status !== "SUSPENDED" && p.status !== "REJECTED" && (
                        <Button data-testid={`suspend-${p.provider_id}`} size="sm" variant="outline" onClick={() => setActionDialog({ provider_id: p.provider_id, name: p.name, newStatus: "SUSPENDED", label: "Suspend" })}
                          className="h-7 rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 font-heading uppercase text-[10px] px-3">
                          <Ban className="w-3 h-3 mr-1" />Suspend
                        </Button>
                      )}
                      {p.status === "PENDING_APPROVAL" && (
                        <Button data-testid={`reject-${p.provider_id}`} size="sm" variant="outline" onClick={() => setActionDialog({ provider_id: p.provider_id, name: p.name, newStatus: "REJECTED", label: "Reject" })}
                          className="h-7 rounded-full border-2 text-slate-500 font-heading uppercase text-[10px] px-3">
                          <XCircle className="w-3 h-3 mr-1" />Reject
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="provider-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-court-orange" />{actionDialog?.label} Provider
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.label} <strong>{actionDialog?.name}</strong>? This will change their platform status.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setActionDialog(null)} className="flex-1 rounded-full border-2">Cancel</Button>
            <Button data-testid="confirm-provider-action" onClick={handleStatusChange} disabled={saving}
              className="flex-1 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase">
              {saving ? "Processing..." : actionDialog?.label}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
